import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useQuery } from 'react-query';
import { apiService, TransformationRule } from '../services/api';

const formatTimestamp = (value?: string | null): string | null => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleString();
};

const RuleLibraryWorkbench: React.FC = () => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [category, setCategory] = React.useState<string>('all');
  const [selectedTags, setSelectedTags] = React.useState<string[]>([]);
  const [sortOrder, setSortOrder] = React.useState<'name' | 'recent' | 'category'>('name');
  const [snackbar, setSnackbar] = React.useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const {
    data,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useQuery(['transformation-rules', 'library'], () => apiService.listTransformationRules(), {
    staleTime: 5 * 60 * 1000,
  });

  const rules = React.useMemo(() => data?.rules ?? [], [data]);

  const categories = React.useMemo(() => {
    const unique = new Set<string>();
    rules.forEach((rule) => {
      if (rule.category) {
        unique.add(rule.category);
      }
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [rules]);

  const availableTags = React.useMemo(() => {
    const all = new Set<string>();
    rules.forEach((rule) => {
      rule.tags?.forEach((tag) => all.add(tag));
    });
    return Array.from(all).sort((a, b) => a.localeCompare(b));
  }, [rules]);

  const librarySummary = React.useMemo(() => {
    const categoryCounts: Record<string, number> = {};
    const tagCounts: Record<string, number> = {};
    rules.forEach((rule) => {
      if (rule.category) {
        categoryCounts[rule.category] = (categoryCounts[rule.category] ?? 0) + 1;
      }
      (rule.tags ?? []).forEach((tag) => {
        tagCounts[tag] = (tagCounts[tag] ?? 0) + 1;
      });
    });
    const categories = Object.entries(categoryCounts).sort((a, b) => b[1] - a[1]);
    const tags = Object.entries(tagCounts).sort((a, b) => b[1] - a[1]).slice(0, 8);
    return { total: rules.length, categories, tags };
  }, [rules]);

  const getComparableTimestamp = (rule: TransformationRule): number => {
    const source = rule.updated_at ?? rule.created_at;
    if (!source) {
      return 0;
    }
    const parsed = new Date(source);
    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
  };

  const filteredRules = React.useMemo(() => {
    if (rules.length === 0) {
      return [];
    }

    const normalizedSearch = searchTerm.trim().toLowerCase();

    const filtered = rules.filter((rule) => {
      if (category !== 'all' && (rule.category ?? '').toLowerCase() !== category.toLowerCase()) {
        return false;
      }

      if (selectedTags.length > 0) {
        const ruleTags = rule.tags ?? [];
        const hasAllSelectedTags = selectedTags.every((tag) => ruleTags.includes(tag));
        if (!hasAllSelectedTags) {
          return false;
        }
      }

      if (normalizedSearch) {
        const haystack = [
          rule.name,
          rule.description ?? '',
          rule.category ?? '',
          rule.expression,
          ...(rule.tags ?? []),
        ]
          .join(' ')
          .toLowerCase();

        if (!haystack.includes(normalizedSearch)) {
          return false;
        }
      }

      return true;
    });

    filtered.sort((a, b) => {
      if (sortOrder === 'recent') {
        return getComparableTimestamp(b) - getComparableTimestamp(a);
      }
      if (sortOrder === 'category') {
        const categoryCompare = (a.category ?? '').localeCompare(b.category ?? '');
        if (categoryCompare !== 0) {
          return categoryCompare;
        }
      }
      return a.name.localeCompare(b.name);
    });

    return filtered;
  }, [rules, searchTerm, category, selectedTags, sortOrder]);

  const resetFilters = () => {
    setSearchTerm('');
    setCategory('all');
    setSelectedTags([]);
  };

  const toggleTag = (tag: string) => {
    setSelectedTags((current) =>
      current.includes(tag) ? current.filter((item) => item !== tag) : [...current, tag]
    );
  };

  const handleCopyExpression = async (rule: TransformationRule) => {
    try {
      await navigator.clipboard.writeText(rule.expression);
      setSnackbar({ message: `Copied expression for ${rule.name}.`, severity: 'success' });
    } catch (error) {
      setSnackbar({ message: 'Unable to copy expression. Copy it manually instead.', severity: 'error' });
    }
  };

  const handleCopyExample = async (rule: TransformationRule) => {
    if (!rule.example || Object.keys(rule.example).length === 0) {
      return;
    }
    try {
      await navigator.clipboard.writeText(JSON.stringify(rule.example, null, 2));
      setSnackbar({ message: `Copied example payload for ${rule.name}.`, severity: 'success' });
    } catch (error) {
      setSnackbar({ message: 'Unable to copy example payload. Copy it manually instead.', severity: 'error' });
    }
  };

  const handleSnackbarClose = () => setSnackbar(null);

  const renderRuleCard = (rule: TransformationRule) => {
    const updatedAt = formatTimestamp(rule.updated_at ?? rule.created_at ?? null);
    const example = rule.example && Object.keys(rule.example).length > 0 ? rule.example : null;

    return (
      <Paper key={rule.rule_id} variant="outlined" sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography variant="h6">{rule.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {rule.description ?? 'No description provided yet.'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="Copy expression to clipboard">
              <IconButton size="small" onClick={() => handleCopyExpression(rule)}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {example && (
              <Tooltip title="Copy example payload">
                <IconButton size="small" onClick={() => handleCopyExample(rule)}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          {rule.category && <Chip label={rule.category} color="primary" size="small" />}
          {(rule.tags ?? []).map((tag) => (
            <Chip key={`${rule.rule_id}-${tag}`} label={tag} variant="outlined" size="small" />
          ))}
        </Stack>

        <Divider flexItem />

        <Typography variant="subtitle2">Expression</Typography>
        <Box
          component="pre"
          sx={{
            bgcolor: 'grey.900',
            color: 'grey.100',
            p: 2,
            borderRadius: 1,
            overflowX: 'auto',
            fontFamily: 'Roboto Mono, Menlo, monospace',
            fontSize: '0.85rem',
            lineHeight: 1.6,
          }}
        >
          {rule.expression}
        </Box>

        {example && (
          <>
            <Typography variant="subtitle2">Example Payload</Typography>
            <Box
              component="pre"
              sx={{
                bgcolor: 'background.default',
                p: 2,
                borderRadius: 1,
                overflowX: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                fontFamily: 'Roboto Mono, Menlo, monospace',
                fontSize: '0.85rem',
                lineHeight: 1.6,
              }}
            >
              {JSON.stringify(example, null, 2)}
            </Box>
          </>
        )}

        {updatedAt && (
          <Typography variant="caption" color="text.secondary">
            Updated {updatedAt}
            {rule.created_by ? ` • ${rule.created_by}` : ''}
          </Typography>
        )}

        <Typography variant="caption" color="text.secondary">
          Rule ID: {rule.rule_id}
        </Typography>
      </Paper>
    );
  };

  return (
    <Box>
      <Stack spacing={3}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Rule Library
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Discover reusable transformation templates, filter by scenario, and copy expressions into your mappings.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="outlined"
              startIcon={<FilterAltOffIcon />}
              onClick={resetFilters}
              disabled={!searchTerm && category === 'all' && selectedTags.length === 0}
            >
              Clear Filters
            </Button>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? 'Refreshing…' : 'Refresh'}
            </Button>
          </Stack>
        </Stack>

        {librarySummary.total > 0 && (
          <Paper variant="outlined" sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Library snapshot
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }}>
              <Box>
                <Typography variant="h5">{librarySummary.total}</Typography>
                <Typography variant="body2" color="text.secondary">
                  total rules available
                </Typography>
              </Box>
              {librarySummary.categories.length > 0 && (
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                  {librarySummary.categories.slice(0, 5).map(([label, count]) => (
                    <Chip key={`category-${label}`} label={`${label} • ${count}`} size="small" />
                  ))}
                </Stack>
              )}
            </Stack>
            {librarySummary.tags.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                {librarySummary.tags.map(([tag, count]) => (
                  <Chip
                    key={`tag-${tag}`}
                    label={`${tag} (${count})`}
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Stack>
            )}
          </Paper>
        )}

        <Paper variant="outlined" sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-end' }}>
            <TextField
              fullWidth
              label="Search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search by name, description, tag, or expression"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              select
              label="Category"
              value={category}
              onChange={(event) => setCategory(event.target.value)}
              size="small"
              sx={{ minWidth: { xs: '100%', md: 200 } }}
            >
              <MenuItem value="all">All categories</MenuItem>
              {categories.map((item) => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Sort by"
              value={sortOrder}
              onChange={(event) => setSortOrder(event.target.value as 'name' | 'recent' | 'category')}
              size="small"
              sx={{ minWidth: { xs: '100%', md: 200 } }}
            >
              <MenuItem value="name">Name (A → Z)</MenuItem>
              <MenuItem value="recent">Recently updated</MenuItem>
              <MenuItem value="category">Category</MenuItem>
            </TextField>
          </Stack>

          {availableTags.length > 0 && (
            <Stack
              direction="row"
              spacing={1}
              mt={2}
              sx={{ flexWrap: 'wrap' }}
            >
              {availableTags.map((tag) => {
                const isSelected = selectedTags.includes(tag);
                return (
                  <Chip
                    key={tag}
                    label={tag}
                    color={isSelected ? 'primary' : 'default'}
                    variant={isSelected ? 'filled' : 'outlined'}
                    onClick={() => toggleTag(tag)}
                    size="small"
                  />
                );
              })}
            </Stack>
          )}
        </Paper>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => refetch()}>Retry</Button>}>
            Unable to load transformation rules right now.
          </Alert>
        ) : filteredRules.length === 0 ? (
          <Alert severity="info">
            No rules match your filters yet. Adjust the search terms or clear filters to see the full library.
          </Alert>
        ) : (
          <Stack spacing={2.5}>
            {filteredRules.map(renderRuleCard)}
          </Stack>
        )}
      </Stack>

      {snackbar && (
        <Snackbar
          open
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export default RuleLibraryWorkbench;
